/**
 * Validacao pura das premissas de diagnostico.
 *
 * Regras:
 * - hourlyRateBrl definido e <= 0 → error INVALID_HOURLY_RATE
 * - monthlyPayrollBrl definido e <= 0 → error INVALID_PAYROLL
 * - toleranceMinutes < 0 → error INVALID_TOLERANCE
 * - toleranceMinutes > 60 → warning HIGH_TOLERANCE
 * - nenhum custo informado → info NO_COST_BASE (ROI ficara oculto)
 *
 * Nao bloqueia o diagnostico inteiro; apenas sinaliza ausencia de base de custo
 * para ocultar calculo de ROI.
 */
import type { DiagnosticAssumptions } from '../../core/domain/canonical.types';
import type { ValidationIssue } from '../../core/validation/validation.types';

/** Valida assumptions; so bloqueia ROI quando custo invalido, nao bloqueia diagnostico inteiro. */
export function validateAssumptions(assumptions: DiagnosticAssumptions): ValidationIssue[] {
  const issues: ValidationIssue[] = [];

  if (assumptions.hourlyRateBrl !== undefined && assumptions.hourlyRateBrl <= 0) {
    issues.push({
      code: 'INVALID_HOURLY_RATE',
      severity: 'error',
      message: 'O custo medio por hora deve ser um valor positivo.',
      source: 'domain',
      recommendation: 'Informe um valor maior que zero, por exemplo: R$ 25,50.',
    });
  }

  if (assumptions.averageGrossSalaryBrl !== undefined && assumptions.averageGrossSalaryBrl <= 0) {
    issues.push({
      code: 'INVALID_PAYROLL',
      severity: 'error',
      message: 'O salário bruto médio deve ser um valor positivo.',
      source: 'domain',
      recommendation: 'Informe o salário bruto médio mensal em reais (ex: R$ 3.500,00).',
    });
  }

  // Legado: monthlyPayrollBrl (deprecated)
  if (
    assumptions.averageGrossSalaryBrl === undefined &&
    assumptions.monthlyPayrollBrl !== undefined &&
    assumptions.monthlyPayrollBrl <= 0
  ) {
    issues.push({
      code: 'INVALID_PAYROLL',
      severity: 'error',
      message: 'A folha mensal total deve ser um valor positivo.',
      source: 'domain',
      recommendation: 'Informe o valor total da folha de pagamento mensal em reais.',
    });
  }

  if (assumptions.toleranceMinutes < 0) {
    issues.push({
      code: 'INVALID_TOLERANCE',
      severity: 'error',
      message: 'A tolerancia de atraso nao pode ser negativa.',
      source: 'domain',
      recommendation: 'Use 0 para sem tolerancia ou um valor positivo em minutos.',
    });
  }

  if (assumptions.toleranceMinutes > 60) {
    issues.push({
      code: 'HIGH_TOLERANCE',
      severity: 'warning',
      message: `Tolerancia de ${assumptions.toleranceMinutes} minutos e incomum. Legislacao trabalhista geralmente considera ate 10 minutos.`,
      source: 'domain',
      recommendation: 'Verifique se o valor esta correto antes de prosseguir.',
    });
  }

  const hasCostBase =
    (assumptions.hourlyRateBrl !== undefined && assumptions.hourlyRateBrl > 0) ||
    (assumptions.averageGrossSalaryBrl !== undefined && assumptions.averageGrossSalaryBrl > 0) ||
    (assumptions.monthlyPayrollBrl !== undefined && assumptions.monthlyPayrollBrl > 0);

  if (!hasCostBase) {
    issues.push({
      code: 'NO_COST_BASE',
      severity: 'info',
      message: 'Nenhuma base de custo informada. O calculo de ROI ficara oculto no relatorio.',
      source: 'domain',
      recommendation:
        'Informe o custo medio por hora ou a folha mensal para habilitar o calculo de impacto financeiro.',
    });
  }

  return issues;
}
